import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useForm, Controller } from "react-hook-form";
import { useLocationSearch } from "../hooks/useLocationSearch.ts";
import LocationTable from './LocationTable';

function GetLocations() {
  const { data, setParams, isLoading, error } = useLocationSearch();
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const { control, handleSubmit, formState: { errors } } = useForm({
    reValidateMode: "onBlur",
    defaultValues: { location: "" }
  });
  const [lastSearch, setLastSearch] = useState("");
  const navigate = useNavigate();

  const handleOnSubmit = (formData) => {
    if (
      formData.location.length >= 3 &&
      formData.location !== lastSearch
    ) {
      setParams({ name: formData.location, count: 100 });
      setSearchSubmitted(true);
      setLastSearch(formData.location);
    }
  };

  useEffect(() => {
    if (!isLoading && searchSubmitted) {
      setSearchSubmitted(false);
    }
  }, [isLoading, searchSubmitted]);

  // Log after fetch
  useEffect(() => {
    if (!isLoading) {
      console.log("Fetch complete. Data:", data);
      if (error) {
        console.error("Fetch error:", error);
      }
    }
  }, [isLoading, data, error]);

  const handleLocationClick = (lat, long) => {
    navigate(`/forecast/${lat}/${long}`);
  };

  return (
    <div
      className="App"
      style={{
        background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)',
        minHeight: '100vh',
        padding: '2rem'
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(handleOnSubmit)}
        sx={{
          mt: 3,
          mb: 3,
          background: '#fff',
          borderRadius: 2,
          boxShadow: 2,
          p: 3
        }}
      >
        <Grid container columns={12} spacing={4}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="location"
              rules={{
                required: "Please enter a location to search",
                minLength: {
                  value: 3,
                  message: "Please enter at least 3 characters"
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Search location"
                  variant="outlined"
                  error={!!errors.location}
                  helperText={errors.location?.message}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
              sx={{ height: '100%' }}
            >
              {isLoading ? <CircularProgress size={24} /> : "Search"}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error fetching location data: {error.message}
        </Alert>
      )}

      {searchSubmitted && !isLoading && data.results && data.results.length === 0 && (
        <Alert severity="info">No locations found. Please try a different search term.</Alert>
      )}

      {data.results && data.results.length > 0 && (
        <LocationTable rows={data.results} onLocationClick={handleLocationClick} />
      )}
    </div>
  );
}

export default GetLocations;